// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Company detail page route module.
 * Provides the main interface for viewing and managing a specific company.
 *
 * Features:
 * - Company information display
 * - Archive control
 * - Edit functionality
 * - Navigation to different company sections
 */
import { ArchiveControl } from '@/components/archive-control';
import { CircularProgressCard } from '@/components/circular-progress-card';
import { IntegrationStatus } from '@/components/integration-status';
import { Link } from '@/components/link';
import { SupportErrorBoundary } from '@/components/support-error-boundary';
import { VerticalNavigationLink } from '@/components/vertical-navigation-link';
import { EditRounded, MergeRounded } from '@/constants/hardfin-standard-icons';
import { getCustomer } from '@/customer/api/get-customer';
import { updateCustomer } from '@/customer/api/update-customer';
import { uploadCustomerLogo } from '@/customer/api/upload-customer-logo';
import { CustomerAssetStatsCard } from '@/customer/components/customer-asset-stats-card';
import { CustomerErrorBoundary } from '@/customer/components/customer-error-boundary';
import { CustomerIcon } from '@/customer/components/customer-icon';
import { CustomerSitesStatsCard } from '@/customer/components/customer-sites-stats-card';
import { CustomerValueStatsCard } from '@/customer/components/customer-value-stats-card';
import { EditCustomerDialog } from '@/customer/components/edit-customer-dialog';
import { MergeCustomerDialog } from '@/customer/components/merge-customer-dialog';
import { CustomerProjectFiltersProvider } from '@/customer/contexts/customer-project-filters-context';
import { useCustomerProjectFilters } from '@/customer/hooks/use-customer-project-filters';
import { useDisclosure } from '@/hooks/use-disclosure';
import { ProjectFilter } from '@/project/api/get-projects';
import { ProjectsFinancialSummary } from '@/project/components/projects-financial-summary';
import { ProjectsSummaryCard } from '@/project/components/projects-summary-card';
import { useProjects } from '@/project/hooks/use-projects';
import {
  Box,
  Breadcrumbs,
  Chip,
  Container,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query';
import { Outlet, createFileRoute, useLocation } from '@tanstack/react-router';
import { Suspense, useEffect, useRef } from 'react';

export const Route = createFileRoute('/_app/companies/$companyKey')({
  component: CompanyRoute,
});

function CompanyRoute() {
  const { companyKey } = Route.useParams();

  return (
    <CustomerErrorBoundary>
      <CustomerProjectFiltersProvider>
        <CompanyContent companyKey={companyKey} />
      </CustomerProjectFiltersProvider>
    </CustomerErrorBoundary>
  );
}

function CompanyContent(props: { companyKey: string }) {
  const queryClient = useQueryClient();
  const editDialog = useDisclosure();
  const mergeDialog = useDisclosure();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const location = useLocation();
  const { locationIds, productIds, statuses, isArchived, resetFilters } =
    useCustomerProjectFilters();

  // Reset filters when navigating away from the projects tab
  useEffect(() => {
    const isOnProjectsTab = location.pathname.endsWith('/projects');
    const hasActiveFilters =
      locationIds.length > 0 ||
      productIds.length > 0 ||
      statuses.length > 0 ||
      isArchived;

    if (!isOnProjectsTab && hasActiveFilters) {
      resetFilters();
    }
  }, [
    location.pathname,
    locationIds.length,
    productIds.length,
    statuses.length,
    isArchived,
    resetFilters,
  ]);

  const { data } = useSuspenseQuery({
    queryKey: ['customer', props.companyKey],
    queryFn: () => getCustomer(props.companyKey),
  });

  // Create filter for financial summary that includes company-specific filters
  const financialSummaryFilter: Omit<
    ProjectFilter,
    'pageSize' | 'pageNumber' | 'sort'
  > = {
    customerIds: [data.customer.id],
  };

  // Fetch projects once and share across child components. The overview
  // card doesn't expose a row-count footer that would flash "0 items", so
  // falling back to `[]` during the cold-load window is safe — the adjacent
  // `CircularProgressCard` handles the loading affordance via Suspense.
  const { data: projectsFetch } = useProjects({
    filter: { customerIds: [data.customer.id], pageSize: 10000 }, // Company overview needs all projects
  });
  const projectsData = projectsFetch ?? [];

  const uploadLogoMutation = useMutation({
    mutationFn: (file: File) => uploadCustomerLogo(file, data.customer.id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['customer'],
      });
    },
  });

  const handleLogoClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      uploadLogoMutation.mutate(file);
    }
  };

  const archiveCompanyMutation = useMutation({
    mutationFn: (isArchived: boolean) =>
      updateCustomer(props.companyKey, { isArchived }),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ['customer'],
      }),
  });

  const archiveToggleTooltip = data.customer.isArchived
    ? 'Unarchive company'
    : 'Archive company. Archiving does not change your company, but archived companies are hidden by default in the company list.';

  return (
    <Stack>
      <EditCustomerDialog
        customer={data.customer}
        isOpen={editDialog.isOpen}
        onClose={editDialog.close}
      />
      {mergeDialog.isOpen && (
        // This component has some state issues that are best resolved by forcing an unmount on close.
        // Specifically, if a company is edited, this dialog will hold a stale copy of that data.
        // This is easier than a complex reset in the component itself.
        <MergeCustomerDialog
          customer={data.customer}
          isOpen={mergeDialog.isOpen}
          onClose={mergeDialog.close}
        />
      )}
      <Container maxWidth="xl">
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          paddingX={3}
          marginTop={3}
        >
          <Breadcrumbs separator="›" sx={{ py: 0.75 }}>
            <Link
              to="/companies"
              variant="body2"
              color="text.secondary"
              sx={{ display: 'block' }}
            >
              Companies
            </Link>
            <Typography variant="body2" color="text.secondary">
              {data.customer.name}
            </Typography>
          </Breadcrumbs>
        </Stack>
        <Stack paddingX={{ xs: 1, md: 3 }} spacing={1}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              {data.customer.integrations.length > 0 && (
                <IntegrationStatus
                  status={data.customer.integrations}
                  entityType="company"
                  entityKey={props.companyKey}
                  entityDisplayName={data.customer.name}
                  onResolved={() => {
                    void queryClient.invalidateQueries({
                      queryKey: ['customer', props.companyKey],
                    });
                  }}
                />
              )}
              <Box
                onClick={handleLogoClick}
                sx={{
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                }}
                margin={0}
                padding={0}
              >
                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: 'none' }}
                  accept="image/*"
                  onChange={handleFileChange}
                />
                <CustomerIcon customer={data.customer} />
              </Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="h5">{data.customer.name}</Typography>
                {data.customer.isArchived && (
                  <Chip size="small" label="Archived" />
                )}
              </Stack>
            </Stack>
            <Stack direction="row" spacing={1}>
              <Tooltip arrow title={archiveToggleTooltip}>
                <span>
                  <ArchiveControl
                    isArchived={data.customer.isArchived}
                    isDisabled={archiveCompanyMutation.isPending}
                    onChange={archiveCompanyMutation.mutate}
                  />
                </span>
              </Tooltip>
              <IconButton size="small" onClick={mergeDialog.open}>
                <MergeRounded fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={editDialog.open}>
                <EditRounded fontSize="small" />
              </IconButton>
            </Stack>
          </Stack>
        </Stack>
        <Stack
          spacing={3}
          marginY={3}
          paddingX={{ xs: 1, md: 3 }}
          direction="column"
        >
          <Box
            display="grid"
            gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }}
            gap={3}
          >
            <SupportErrorBoundary message="An error occurred loading company value">
              <Suspense fallback={<CircularProgressCard />}>
                <CustomerValueStatsCard customer={data.customer} />
              </Suspense>
            </SupportErrorBoundary>

            <SupportErrorBoundary message="An error occurred loading projects summary">
              <Suspense fallback={<CircularProgressCard />}>
                <ProjectsSummaryCard projects={projectsData} />
              </Suspense>
            </SupportErrorBoundary>
          </Box>

          <Box
            display="grid"
            gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }}
            gap={3}
          >
            <SupportErrorBoundary message="An error occurred loading asset statistics">
              <Suspense fallback={<CircularProgressCard />}>
                <CustomerAssetStatsCard customer={data.customer} />
              </Suspense>
            </SupportErrorBoundary>

            <SupportErrorBoundary message="An error occurred loading site statistics">
              <Suspense fallback={<CircularProgressCard />}>
                <CustomerSitesStatsCard customer={data.customer} />
              </Suspense>
            </SupportErrorBoundary>
          </Box>

          <SupportErrorBoundary message="An error occurred loading company details">
            <Box>
              <ProjectsFinancialSummary filter={financialSummaryFilter} />
            </Box>
          </SupportErrorBoundary>

          {/* Company details navigation and content */}
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
            <Stack direction={{ xs: 'row', md: 'column' }}>
              <VerticalNavigationLink
                to="/companies/$companyKey"
                params={{ companyKey: props.companyKey }}
                activeOptions={{ exact: true }}
              >
                Basic info
              </VerticalNavigationLink>
              <VerticalNavigationLink
                to="/companies/$companyKey/contacts"
                params={{ companyKey: props.companyKey }}
              >
                Contacts
              </VerticalNavigationLink>
              <VerticalNavigationLink
                to="/companies/$companyKey/projects"
                params={{ companyKey: props.companyKey }}
              >
                Projects
              </VerticalNavigationLink>
              <VerticalNavigationLink
                to="/companies/$companyKey/locations"
                params={{ companyKey: props.companyKey }}
              >
                Locations
              </VerticalNavigationLink>
              <VerticalNavigationLink
                to="/companies/$companyKey/location-assets"
                params={{ companyKey: props.companyKey }}
              >
                Assets on location
              </VerticalNavigationLink>
              <VerticalNavigationLink
                to="/companies/$companyKey/owned-assets"
                params={{ companyKey: props.companyKey }}
              >
                Assets owned
              </VerticalNavigationLink>
              <VerticalNavigationLink
                to="/companies/$companyKey/audit"
                params={{ companyKey: props.companyKey }}
              >
                Audit log
              </VerticalNavigationLink>
            </Stack>

            <Box sx={{ flexGrow: 1 }}>
              <Suspense fallback={<CircularProgressCard />}>
                <Outlet />
              </Suspense>
            </Box>
          </Stack>
        </Stack>
      </Container>
    </Stack>
  );
}
